import { useMemo } from 'react'

import { useAppStore } from '@/hooks'
import { useSelectedSystem } from '@/pages/User/pages/Home/components/TreePanel/TreePanelContext'
import { useGetBranchesQuery } from '@/rtkApi'

import { BRANCH_DEFAULT_STATUSES, BranchFilterSession } from '../branchFilterUi'
import { mapBranchDto } from '../mapBranch'
import { BRANCH_LIST_PAGE_SIZE } from '../types'

type UseBranchesOptions = {
	filter?: BranchFilterSession
	search?: string
	skip?: boolean
}

export const useBranches = ({
	filter,
	search,
	skip = false,
}: UseBranchesOptions = {}) => {
	const { selectedSystemId } = useSelectedSystem()
	const nodes = useAppStore((state) => state.node.nodes)
	const systemId = selectedSystemId ? Number(selectedSystemId) : undefined
	const hasSystemId = systemId != null && !Number.isNaN(systemId)
	const systemName = hasSystemId
		? nodes[systemId]?.name || nodes[systemId]?.code || ''
		: ''

	const { data, isError, isLoading } = useGetBranchesQuery(
		{
			systemId: systemId as number,
			search: search?.trim() || undefined,
			status: filter?.statuses.length
				? filter.statuses
				: BRANCH_DEFAULT_STATUSES,
			protectedBranch: filter?.protectedOnly || undefined,
			pageNumber: 1,
			pageSize: BRANCH_LIST_PAGE_SIZE,
		},
		{ skip: !hasSystemId || skip },
	)

	const branches = useMemo(
		() =>
			(data?.content ?? [])
				.filter((branch) => branch.id != null)
				.map((branch) => mapBranchDto(branch, systemName)),
		[data?.content, systemName],
	)

	return {
		branches,
		hasSystemId,
		isError,
		isLoading: hasSystemId && isLoading,
		systemId: hasSystemId ? systemId : undefined,
		totalElements: data?.totalElements ?? 0,
	}
}
